// SessionContext — fetches /auth/me when the user is authenticated and
// exposes the resulting UserSession (displayName, roles, etc.) via useSession().
//
// Why a separate context (not inlined into AuthContext): keeps the auth
// state machine (useAuthFlow) focused on Cognito flows + token lifecycle.
// /auth/me is application-level data, not auth-flow control.
//
// Behavior:
//  - When useAuth().status flips to 'authenticated', fire one /auth/me call.
//  - Cache the resulting session in state until status flips away.
//  - Re-fetch on demand via refetch() (e.g. after a profile edit).
//  - During the fetch, isLoading is true; consumers can show skeletons.
//  - On error, fall back to null session; consumers degrade gracefully.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UserSession } from '@gc-coach/shared';
import { getMe } from '../api/endpoints.js';
import { useAuth } from './AuthContext.js';

export interface SessionApi {
  /** Current session, or null while loading / unauthenticated / on error. */
  session: UserSession | null;
  /** True while the /auth/me fetch is in flight. */
  isLoading: boolean;
  /** Force a re-fetch (e.g. after the current user edits their own profile). */
  refetch: () => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [session, setSession] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getMe();
      if (result.ok) {
        setSession(result.data.session);
      } else {
        // Mongo unreachable / synthetic-fallback session would still come
        // back as ok:true with default roles. Hard errors (401, network)
        // clear the session — UserMenu etc. show a degraded state.
        setSession(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on authenticated → session-missing.
  // Preserve the cached session through session-expired (so the
  // SessionExpiredModal can read the user's email/displayName even when
  // the auth state has flipped away from 'authenticated'). Clear only on
  // a true sign-out / unauthenticated state.
  useEffect(() => {
    if (status.kind === 'authenticated') {
      if (!session) void fetchSession();
    } else if (status.kind === 'unauthenticated') {
      setSession(null);
    }
    // session-expired, mfa-challenge, etc. — keep the cached session as-is.
  }, [status.kind, session, fetchSession]);

  const api = useMemo<SessionApi>(
    () => ({ session, isLoading, refetch: fetchSession }),
    [session, isLoading, fetchSession],
  );

  return <SessionContext.Provider value={api}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be called inside <SessionProvider>');
  }
  return ctx;
}
